import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Autocomplete,
  TextField,
  Chip,
  IconButton,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import InputAdornment from '@mui/material/InputAdornment';
import PageLayout from '../components/common/PageLayout';
import { get } from '../utils/api';
import { fetchPledgesByProduct } from '../utils/pledgeReports';
import { PledgesCustomerDialog } from '../components/pledges/PledgesCustomerDialog';

function formatAmount(n) {
  const v = Number(n);
  if (!isFinite(v)) return '--';
  return v.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export default function PledgesPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const scopeParam = searchParams.get('scope');
  const initialKotilinga =
    scopeParam === 'kotilinga' ? true : scopeParam === 'temple' ? false : false;
  const isScopeLocked = scopeParam === 'kotilinga' || scopeParam === 'temple';
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [pledgesByProduct, setPledgesByProduct] = React.useState([]);
  const [page, setPage] = React.useState(1);
  const [perPage] = React.useState(40);
  const [selectedProductFilter, setSelectedProductFilter] = React.useState(null);
  const [statusFilter, setStatusFilter] = React.useState('all'); // all|pending|completed
  const [kotilingaFilter, setKotilingaFilter] = React.useState(initialKotilinga);
  const [expandedProducts, setExpandedProducts] = React.useState({});
  const [variationsMap, setVariationsMap] = React.useState({});
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogProduct, setDialogProduct] = React.useState(null);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = React.useState('');

  // Debounce search so we refetch by-product with q= after user stops typing
  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchQuery(searchQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const buildRangeParams = () => {
    // No date range filtering for pledges page - show all
    return '';
  };

  const loadPledges = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      // Add status filter - all means both pledge-pending and pledge-completed
      params.append('status', statusFilter);
      
      // Temple/Kotilinga filter — backend resolves scope from KOTILINGA_CATEGORY_SLUGS
      if (kotilingaFilter) {
        params.append('kotilinga', 'true');
      } else {
        params.append('temple', 'true');
      }
      if (debouncedSearchQuery) params.append('q', debouncedSearchQuery);

      // Use canonical endpoint/helper shared with dashboards.
      const { rows: data } = await fetchPledgesByProduct(get, Object.fromEntries(params.entries()));
      
      // Sort by balance_total descending (max balance at top) - same as dashboard
      const sorted = [...data].sort((a, b) => {
        const balanceA = Number(a.balance_total || 0);
        const balanceB = Number(b.balance_total || 0);
        return balanceB - balanceA;
      });

      setPledgesByProduct(sorted);
    } catch (e) {
      console.error('[PledgesPage] Failed to load pledges:', e);
      setError(e?.message || 'Failed to load pledges');
      setPledgesByProduct([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, kotilingaFilter, debouncedSearchQuery]);

  React.useEffect(() => {
    loadPledges();
  }, [loadPledges]);

  React.useEffect(() => {
    if (!location.state?.orderEditSaved) return;
    loadPledges();
    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
  }, [location.state?.orderEditSaved]);

  // Clear variations cache when status filter changes
  React.useEffect(() => {
    setVariationsMap({});
    // Also collapse all expanded products so they refetch with new filter
    setExpandedProducts({});
  }, [statusFilter]);

  const toggleExpandProduct = async (productId) => {
    const isExpanded = !!expandedProducts[productId];
    if (isExpanded) {
      setExpandedProducts(prev => {
        const next = { ...prev };
        delete next[productId];
        return next;
      });
      return;
    }

    setExpandedProducts(prev => ({ ...prev, [productId]: true }));

    // Always refetch variations to ensure they match current status filter
    // Don't use cache since status filter might have changed

    try {
      const params = new URLSearchParams();
      params.append('status', statusFilter);
      
      // Add Temple/Kotilinga filter - backend reads category IDs from env variable
      if (kotilingaFilter) {
        params.append('kotilinga', 'true');
      } else {
        params.append('temple', 'true');
      }

      const res = await get(`/ldb/reports/pledges/by-product/${productId}/variations?${params.toString()}`);
      const rows = Array.isArray(res?.data) ? res.data : [];
      
      // Transform to match expected format - same as dashboard
      const arr = rows.map(r => ({
        variation_id: r.variation_id || 0,
        variation_name: r.variation_name || r.name || '',
        pledge_total: Number(r.pledge_total || r.total || 0),
        paid_total: Number(r.paid_total || 0),
        balance_total: Number(r.balance_total || 0),
        donors_count: Number(r.donors_count || r.count || 0),
        pledge_count: Number(r.pledge_count || 0)
      }))
      .filter(x => Number(x.pledge_total || 0) > 0)
      .sort((a, b) => {
        const balanceA = Number(a.balance_total || 0);
        const balanceB = Number(b.balance_total || 0);
        return balanceB - balanceA;
      });

      setVariationsMap(prev => ({ ...prev, [productId]: arr }));
    } catch (e) {
      console.error('[PledgesPage] Failed to load variations:', e);
      setVariationsMap(prev => ({ ...prev, [productId]: [] }));
    }
  };

  const openDialog = (product, variation = null) => {
    setDialogProduct({ ...product, variation });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setDialogProduct(null);
  };

  const handleDownloadCSV = async () => {
    try {
      // Build params matching how the page loads data (using kotilinga/temple flags)
      const params = new URLSearchParams();
      params.append('status', statusFilter);
      
      if (kotilingaFilter) {
        params.append('kotilinga', 'true');
      } else {
        params.append('temple', 'true');
      }
      
      // Always fetch products from API to match what the page shows
      const productsRes = await get(`/ldb/reports/pledges/by-product?${params.toString()}`);
      let products = Array.isArray(productsRes?.data) ? productsRes.data : [];
      
      // Apply product filter if one is selected
      if (selectedProductFilter && selectedProductFilter.product_id) {
        products = products.filter(p => p.product_id === selectedProductFilter.product_id);
      }
      
      console.log('[PledgesPage][Download] Processing', products.length, 'product(s)');

      // Collect all customer-level data
      const allCustomerData = [];

      for (const product of products) {
        const pid = product.product_id;
        console.log(`[PledgesPage][Download] Processing product ${pid}: ${product.product_name}`);

        // Fetch variations using same params as page (kotilinga/temple flags)
        const varParams = new URLSearchParams();
        varParams.append('status', statusFilter);
        if (kotilingaFilter) {
          varParams.append('kotilinga', 'true');
        } else {
          varParams.append('temple', 'true');
        }
        
        const variationsRes = await get(`/ldb/reports/pledges/by-product/${pid}/variations?${varParams.toString()}`);
        const variations = Array.isArray(variationsRes?.data) ? variationsRes.data : [];
        console.log(`[PledgesPage][Download] Product ${pid} has ${variations.length} variations`);

        for (const variation of variations) {
          const vid = variation.variation_id || 0;

          // Fetch customers - match PledgesCustomerDialog which only passes status (no kotilinga/temple flags)
          // The customer endpoint filters are applied at product/variation level, so flags aren't needed here
          try {
            const customerParams = new URLSearchParams();
            customerParams.append('status', statusFilter);
            // Note: PledgesCustomerDialog doesn't pass kotilinga/temple flags, and it works
            // The filtering happens at the product/variation level, not customer level
            
            const customersRes = await get(`/ldb/reports/pledges/by-product/${pid}/variations/${vid}/customers?${customerParams.toString()}`);
            const customers = Array.isArray(customersRes?.data) ? customersRes.data : [];
            console.log(`[PledgesPage][Download] Product ${pid}, Variation ${vid} has ${customers.length} customers`);

            for (const customer of customers) {
              // Use product_url from product data, or construct fallback link
              const productUrl = product.product_url || '';
              // If product_url exists, use it (user can choose variation after clicking)
              // Otherwise, fallback to constructed link
              const paymentLink = productUrl;

              // Build customer name from billing fields (matching what backend returns)
              const customerName = customer.name || 
                (customer.billing_first_name || customer.billing_last_name 
                  ? `${customer.billing_first_name || ''} ${customer.billing_last_name || ''}`.trim() 
                  : customer.billing_email || 'Unknown');
              
              allCustomerData.push({
                customerName: customerName,
                phone: customer.billing_phone || '',
                email: customer.billing_email || customer.email || '',
                productName: product.product_name || `Product #${pid}`,
                variationName: variation.variation_name || variation.name || (vid ? `Variation #${vid}` : ''),
                productUrl: productUrl,
                paymentLink: paymentLink,
                pledged: Number(customer.pledge_total || 0),
                paid: Number(customer.paid_total || 0),
                balance: Number(customer.balance_total || 0)
              });
            }
          } catch (e) {
            console.error(`[PledgesPage][Download] Failed to load customers for product ${pid}, variation ${vid}:`, e);
          }
        }
      }

      console.log(`[PledgesPage][Download] Total customer records collected: ${allCustomerData.length}`);
      
      if (allCustomerData.length === 0) {
        console.warn('[PledgesPage][Download] No customer data found. The CSV will contain only headers.');
      }

      // Generate CSV (same format as dashboard)
      const headers = ['Customer Name', 'Phone', 'Email', 'Product Name', 'Variation Name', 'Product URL', 'Payment Link', 'Pledged', 'Paid', 'Balance'];
      const csvRows = [
        headers.join(','),
        ...allCustomerData.map(row => [
          `"${(row.customerName || '').replace(/"/g, '""')}"`,
          `"${(row.phone || '').replace(/"/g, '""')}"`,
          `"${(row.email || '').replace(/"/g, '""')}"`,
          `"${(row.productName || '').replace(/"/g, '""')}"`,
          `"${(row.variationName || '').replace(/"/g, '""')}"`,
          `"${(row.productUrl || '').replace(/"/g, '""')}"`,
          `"${(row.paymentLink || '').replace(/"/g, '""')}"`,
          (Number(row.pledged) || 0).toFixed(2),
          (Number(row.paid) || 0).toFixed(2),
          (Number(row.balance) || 0).toFixed(2)
        ].join(','))
      ];

      const csvContent = csvRows.join('\n');
      console.log(`[PledgesPage][Download] Generated CSV with ${csvRows.length - 1} data rows (plus 1 header row)`);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `pledges_${kotilingaFilter ? 'kotilinga' : 'temple'}_${statusFilter}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error('[PledgesPage] Failed to download CSV:', e);
      alert('Failed to download CSV. Please try again.');
    }
  };

  // Filter and paginate: search is done server-side via q; only product dropdown filter is client-side
  const filteredProducts = React.useMemo(() => {
    let filtered = pledgesByProduct;
    if (selectedProductFilter) {
      filtered = filtered.filter(p => p.product_id === selectedProductFilter.product_id);
    }
    return filtered;
  }, [pledgesByProduct, selectedProductFilter]);

  // Calculate grand totals - use filtered products (respects Temple/Kotilinga filter)
  const grandTotals = React.useMemo(() => {
    return filteredProducts.reduce((acc, p) => ({
      pledge_total: acc.pledge_total + Number(p.pledge_total || 0),
      paid_total: acc.paid_total + Number(p.paid_total || 0),
      balance_total: acc.balance_total + Number(p.balance_total || 0),
      donors_count: acc.donors_count + Number(p.donors_count || 0),
      pledge_count: acc.pledge_count + Number(p.pledge_count || 0), // Sum of pledge counts across all products
    }), { pledge_total: 0, paid_total: 0, balance_total: 0, donors_count: 0, pledge_count: 0 });
  }, [filteredProducts]);

  const paginatedProducts = React.useMemo(() => {
    const start = (page - 1) * perPage;
    const end = start + perPage;
    return filteredProducts.slice(start, end);
  }, [filteredProducts, page, perPage]);

  const totalPages = Math.ceil(filteredProducts.length / perPage);

  return (
    <PageLayout contentMaxWidth="xl" showHeaderImage={false} showOrgHeader>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Pledges</Typography>
      </Box>

      {/* Controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        {/* Free-text search: server filters by product, variation, or sponsor (debounced refetch) */}
        <TextField
          size="small"
          placeholder="Search by product, variation, or sponsor"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(1);
          }}
          sx={{ width: 280, maxWidth: 360 }}
          inputProps={{ 'aria-label': 'Search pledges by product, variation, or sponsor' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        {/* Product filter dropdown */}
        <Autocomplete
          size="small"
          options={pledgesByProduct || []}
          getOptionLabel={(option) => option.product_name || `Product #${option.product_id}`}
          value={selectedProductFilter}
          onChange={(event, newValue) => {
            setSelectedProductFilter(newValue);
            setPage(1);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Filter by product"
              sx={{ width: 260, maxWidth: 340 }}
            />
          )}
          isOptionEqualToValue={(option, value) => option.product_id === value?.product_id}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Typography variant="body2">{option.product_name || `Product #${option.product_id}`}</Typography>
            </Box>
          )}
        />

        {/* Pledge Status - Dropdown */}
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Pledge Status</InputLabel>
          <Select
            value={statusFilter}
            label="Pledge Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
          </Select>
        </FormControl>

        {/* Temple/Kotilinga Toggle (hidden when scope is locked via URL) */}
        {!isScopeLocked && (
          <ToggleButtonGroup
            value={kotilingaFilter ? 'kotilinga' : 'temple'}
            exclusive
            onChange={(e, value) => {
              if (value !== null) {
                setKotilingaFilter(value === 'kotilinga');
              }
            }}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                color: 'text.secondary',
                bgcolor: 'grey.200',
                '&.Mui-selected': {
                  color: 'white',
                  bgcolor: '#42a5f5',
                  '&:hover': {
                    bgcolor: '#1976d2',
                  },
                },
              },
            }}
          >
            <ToggleButton value="temple">Temple</ToggleButton>
            <ToggleButton value="kotilinga">Kotilinga</ToggleButton>
          </ToggleButtonGroup>
        )}

        {/* Clear filters (same pattern as KotilingaInventoryPage) */}
        {(searchQuery || selectedProductFilter) && (
          <Chip
            label="Clear filters"
            size="small"
            onClick={() => {
              setSearchQuery('');
              setSelectedProductFilter(null);
              setPage(1);
            }}
          />
        )}

        {/* Download Button */}
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadCSV}
        >
          Download CSV
        </Button>
      </Box>

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      {/* Grand Totals */}
      {pledgesByProduct.length > 0 && (
        <Paper elevation={0} sx={{ p: 2.5, mb: 2, borderRadius: 2, bgcolor: 'primary.light', border: '2px solid', borderColor: 'primary.main', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: 'primary.contrastText' }}>
            {kotilingaFilter ? 'Kotilinga Pledges' : 'Temple Pledges'}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 2 }}>
            <Box>
              <Typography variant="caption" sx={{ color: 'primary.contrastText', opacity: 0.9, display: 'block', mb: 0.5 }}>No. of Pledges</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.contrastText' }}>{grandTotals.pledge_count}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'primary.contrastText', opacity: 0.9, display: 'block', mb: 0.5 }}>Total Donors</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.contrastText' }}>{grandTotals.donors_count}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'primary.contrastText', opacity: 0.9, display: 'block', mb: 0.5 }}>Total Pledges</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.contrastText' }}>{formatAmount(grandTotals.pledge_total)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'primary.contrastText', opacity: 0.9, display: 'block', mb: 0.5 }}>Total Paid</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.contrastText' }}>{formatAmount(grandTotals.paid_total)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'primary.contrastText', opacity: 0.9, display: 'block', mb: 0.5 }}>Total Balance</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.contrastText' }}>{formatAmount(grandTotals.balance_total)}</Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Table */}
      <Paper elevation={0} sx={{ p: 2, borderRadius: 3, bgcolor: '#f7faff', border: '1px solid', borderColor: 'divider', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
              {/* Header */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 100px 140px 140px 140px', bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" sx={{ p: 1, fontWeight: 700 }}>Product Name</Typography>
                <Typography variant="caption" sx={{ p: 1, fontWeight: 700, textAlign: 'right' }}>No of Pledges</Typography>
                <Typography variant="caption" sx={{ p: 1, fontWeight: 700, textAlign: 'right' }}>Total Pledges</Typography>
                <Typography variant="caption" sx={{ p: 1, fontWeight: 700, textAlign: 'right' }}>Paid</Typography>
                <Typography variant="caption" sx={{ p: 1, fontWeight: 700, textAlign: 'right' }}>Balance</Typography>
              </Box>

              {/* Rows */}
              {paginatedProducts.map((p, i) => {
                const isExpanded = !!expandedProducts[p.product_id];
                const variations = variationsMap[p.product_id] || [];

                return (
                  <React.Fragment key={p.product_id}>
                    {/* Product Row */}
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 100px 140px 140px 140px',
                        borderTop: i > 0 ? '1px solid' : 'none',
                        borderColor: 'divider',
                        bgcolor: i % 2 === 0 ? 'transparent' : 'action.hover',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.selected' }
                      }}
                      onClick={() => toggleExpandProduct(p.product_id)}
                    >
                      <Typography variant="body2" sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {isExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                        {p.product_name || `Product #${p.product_id}`}
                      </Typography>
                      <Typography variant="body2" sx={{ p: 1, textAlign: 'right', fontWeight: 600 }}>
                        {Number(p.pledge_count || 0).toLocaleString()}
                      </Typography>
                      <Typography variant="body2" sx={{ p: 1, textAlign: 'right' }}>{formatAmount(p.pledge_total)}</Typography>
                      <Typography variant="body2" sx={{ p: 1, textAlign: 'right' }}>{formatAmount(p.paid_total)}</Typography>
                      <Typography variant="body2" sx={{ p: 1, textAlign: 'right', fontWeight: 600 }}>{formatAmount(p.balance_total)}</Typography>
                    </Box>

                    {/* Variations */}
                    {isExpanded && (
                      <Box sx={{ pl: 4, bgcolor: 'background.paper' }}>
                        {variations.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>Loading variations...</Typography>
                        ) : (
                          <>
                            {variations.map((v, vIdx) => {
                              // Calculate product-level totals for variations
                              const variationPledgeCount = Number(v.pledge_count || 0);
                              return (
                                <Box
                                  key={v.variation_id}
                                  sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '2fr 100px 140px 140px 140px',
                                    borderTop: vIdx > 0 ? '1px solid' : '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: vIdx % 2 === 0 ? 'transparent' : 'action.hover',
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: 'action.selected' }
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openDialog(p, v);
                                  }}
                                >
                                  <Typography variant="body2" sx={{ p: 1, pl: 2, color: '#1976d2' }}>
                                    {v.variation_id === 0 || v.variation_id === null 
                                      ? (v.variation_name || p.product_name || 'Default')
                                      : (v.variation_name || `Variation #${v.variation_id}`)}
                                  </Typography>
                                  <Typography variant="body2" sx={{ p: 1, textAlign: 'right', fontWeight: 600 }}>
                                    {variationPledgeCount.toLocaleString()}
                                  </Typography>
                                  <Typography variant="body2" sx={{ p: 1, textAlign: 'right' }}>{formatAmount(v.pledge_total || v.total || 0)}</Typography>
                                  <Typography variant="body2" sx={{ p: 1, textAlign: 'right' }}>{formatAmount(v.paid_total || 0)}</Typography>
                                  <Typography variant="body2" sx={{ p: 1, textAlign: 'right', fontWeight: 600 }}>{formatAmount(v.balance_total || 0)}</Typography>
                                </Box>
                              );
                            })}
                            {/* Variation Totals Row */}
                            {variations.length > 0 && (() => {
                              const variationTotals = variations.reduce((acc, v) => ({
                                pledge_count: acc.pledge_count + Number(v.pledge_count || 0),
                                pledge_total: acc.pledge_total + Number(v.pledge_total || v.total || 0),
                                paid_total: acc.paid_total + Number(v.paid_total || 0),
                                balance_total: acc.balance_total + Number(v.balance_total || 0)
                              }), { pledge_count: 0, pledge_total: 0, paid_total: 0, balance_total: 0 });
                              return (
                                <Box
                                  sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '2fr 100px 140px 140px 140px',
                                    borderTop: '2px solid',
                                    borderColor: 'primary.main',
                                    bgcolor: 'primary.light',
                                    mt: 0.5
                                  }}
                                >
                                  <Typography variant="body2" sx={{ p: 1, pl: 2, fontWeight: 700, color: 'primary.contrastText' }}>
                                    Variation Total
                                  </Typography>
                                  <Typography variant="body2" sx={{ p: 1, textAlign: 'right', fontWeight: 700, color: 'primary.contrastText' }}>
                                    {variationTotals.pledge_count.toLocaleString()}
                                  </Typography>
                                  <Typography variant="body2" sx={{ p: 1, textAlign: 'right', fontWeight: 700, color: 'primary.contrastText' }}>
                                    {formatAmount(variationTotals.pledge_total)}
                                  </Typography>
                                  <Typography variant="body2" sx={{ p: 1, textAlign: 'right', fontWeight: 700, color: 'primary.contrastText' }}>
                                    {formatAmount(variationTotals.paid_total)}
                                  </Typography>
                                  <Typography variant="body2" sx={{ p: 1, textAlign: 'right', fontWeight: 700, color: 'primary.contrastText' }}>
                                    {formatAmount(variationTotals.balance_total)}
                                  </Typography>
                                </Box>
                              );
                            })()}
                          </>
                        )}
                      </Box>
                    )}
                  </React.Fragment>
                );
              })}

              {paginatedProducts.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>No pledges found</Typography>
              )}
            </Box>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, value) => setPage(value)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Customer Dialog */}
      <PledgesCustomerDialog
        open={dialogOpen}
        onClose={closeDialog}
        product={dialogProduct}
        statusFilter={statusFilter}
        onOpenOrderEdit={(orderId) => {
          if (orderId == null || orderId === '') return;
          closeDialog();
          navigate(`/orders/${orderId}/edit`, { state: { returnTo: `${location.pathname}${location.search}` } });
        }}
      />
    </PageLayout>
  );
}

